import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Badge,
  Avatar,
  Modal,
  message,
  Divider,
  Descriptions,
  Alert,
  Spin,
  Input,
  Select,
  Tooltip
} from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  VideoCameraOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  BuildOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { GET_APPOINTMENT, GET_USER_APPOINTMENTS, GET_DELIVERY_CONTACT_LIST } from '../graphql/queries';
import { useTranslation } from '../hooks/useTranslation';
import FloatingAgoraCall from '../components/FloatingAgoraCall';
import AppointmentQuestionnaireResponseSection from '../components/Appointment/AppointmentQuestionnaireResponseSection';
import ProviderInformationCard from '../components/Appointment/ProviderInformationCard';
import PatientInformationCard from '../components/Appointment/PatientInformationCard';
import AppointmentTimeline from '../components/Appointment/AppointmentTimeline';
import DocumentUpload from '../components/DocumentUpload';
import TransactionSection from '../components/Appointment/TransactionSection';
import DeliveryAddressCard from '../components/Appointment/DeliveryAddressCard';
import AppointmentQuickActions from '../components/Appointment/QuickActions/AppointmentQuickActions';
import InsuranceClaimCard from '../components/Appointment/InsuranceClaimCard';
import ProviderSelectionModal from '../components/ProviderSelectionModal';
import JDExpressConfirmationModal from '../components/Appointment/JDExpressConfirmationModal';
import { SfExpressOrder } from '../types/sf-express.types';
import dayjs from 'dayjs';
import {
  CONFIRM_APPOINTMENT,
  CANCEL_APPOINTMENT,
  START_APPOINTMENT,
  COMPLETE_APPOINTMENT,
  MARK_APPOINTMENT_NO_SHOW,
  UPDATE_APPOINTMENT,
  CLOUD_PRINT_SF_EXPRESS_WAYBILL,
  CLOUD_PRINT_JD_EXPRESS_WAYBILL,
  PRECHECK_JD_EXPRESS_ORDER,
  UPDATE_ADDRESS
} from '../graphql/mutations';
import { useAuth } from '../hooks/useAuth';
import { useAppointmentSSESubscription, AppointmentEvent } from '../lib/sse';
import type { GetAppointmentQuery, GetUserAppointmentsQuery } from '../generated/graphql';
import { HK_REGIONS_AND_DISTRICTS } from '../utils/hk-regions';




// SF Express Order Creation Mutation
const CREATE_SF_EXPRESS_ORDER = gql`
  mutation CreateSfExpressOrderFromAppointment($input: CreateSfExpressOrderFromAppointmentDto!) {
    createSfExpressOrderFromAppointment(input: $input) {
      success
      errorCode
      errorMsg
      data {
        orderId
        trackingNumber
        waybillNo
      }
    }
  }
`;

// JD Express Order Creation Mutation
const CREATE_JD_EXPRESS_ORDER = gql`
  mutation CreateJdExpressOrderFromAppointment($input: CreateJdExpressOrderFromAppointmentDto!) {
    createJdExpressOrderFromAppointment(input: $input) {
      success
      errorCode
      errorMsg
      data {
        orderId
        trackingNumber
        waybillNo
        estimatedDelivery
        freight
        insuranceFee
        totalFee
        originCode
        destCode
        remark
      }
    }
  }
`;

const { Title, Text } = Typography;
const { TextArea } = Input;

// Helper function to calculate age from birthdate
const calculateAge = (birthdate: string): number => {
  const today = new Date();
  const birthDate = new Date(birthdate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Helper function to get patient display name
const getPatientDisplayName = (patient: { first_name_en?: string; last_name_en?: string; first_name_zh_hk?: string; last_name_zh_hk?: string } | undefined, t: (k: string, v?: any) => string): string => {
  if (!patient) return t('common.na');
  const englishName = `${patient.first_name_en || ''} ${patient.last_name_en || ''}`.trim();
  const chineseName = `${patient.last_name_zh_hk || ''}${patient.first_name_zh_hk || ''}`.trim();
  if (englishName && chineseName) return `${englishName} (${chineseName})`;
  if (englishName) return englishName;
  if (chineseName) return chineseName;
  return t('common.na');
};

// Helper function to get gender display text
const getGenderDisplay = (gender: string, t: (k: string) => string): string => {
  switch (gender) {
    case 'male': return t('patients.male');
    case 'female': return t('patients.female');
    case 'other': return t('patients.other');
    default: return t('common.na');
  }
};

// Helper function to get relationship display text
const getRelationshipDisplay = (relationship: string, t: (k: string) => string): string => {
  switch (relationship) {
    case 'self': return t('patients.relationships.self');
    case 'spouse': return t('patients.relationships.spouse');
    case 'child': return t('patients.relationships.child');
    case 'parent': return t('patients.relationships.parent');
    case 'sibling': return t('patients.relationships.sibling');
    case 'other': return t('patients.relationships.other');
    default: return t('common.na');
  }
};

// Helper function to get localized region name
const getLocalizedRegionName = (regionCode: string, locale: string = 'zh-HK'): string => {
  if (!regionCode) return '';
  const region = HK_REGIONS_AND_DISTRICTS.find(r => r.code === regionCode);
  if (!region) return regionCode;
  
  switch (locale) {
    case 'zh-CN':
      return region.name_zh_cn;
    case 'en':
      return region.name_en;
    case 'zh-HK':
    default:
      return region.name_zh_hk;
  }
};

// Helper function to get localized district name
const getLocalizedDistrictName = (regionCode: string, districtCode: string, locale: string = 'zh-HK'): string => {
  if (!regionCode || !districtCode) return '';
  const region = HK_REGIONS_AND_DISTRICTS.find(r => r.code === regionCode);
  if (!region) return districtCode;
  
  const district = region.districts.find(d => d.code === districtCode);
  if (!district) return districtCode;
  
  switch (locale) {
    case 'zh-CN':
      return district.name_zh_cn;
    case 'en':
      return district.name_en;
    case 'zh-HK':
    default:
      return district.name_zh_hk;
  }
};

// Helper function to format address
const formatAddress = (address: any, t: (k: string, options?: { value: string }) => string, locale: string = 'zh-HK'): string => {
  if (!address) return t('common.na');
  
  const localizedRegion = address.region ? getLocalizedRegionName(address.region, locale) : '';
  const localizedDistrict = address.district && address.region ? 
    getLocalizedDistrictName(address.region, address.district, locale) : 
    address.district;
  
  const parts = [
    address.street_address,
    address.building_name,
    address.block && `${t('addresses.value.block', { value: address.block })}`,
    address.floor && `${t('addresses.value.floor', { value: address.floor })}`,
    address.unit && `${t('addresses.value.unit', { value: address.unit })}`,
    localizedDistrict,
    localizedRegion,
    address.postal_code,
    address.country
  ].filter(Boolean);
  
  return parts.join(', ') || t('common.na');
};

// Helper function to get delivery address string
const getDeliveryAddress = (deliveryAddress: any, t: (k: string, options?: { value: string }) => string,    locale: string = 'zh-HK'): string => {
  if (!deliveryAddress) return '';
  
  // If override_address exists, use it
  if (deliveryAddress.override_address) {
    return deliveryAddress.override_address;
  }
  
  // Otherwise format the address
  const localizedRegion = deliveryAddress.region ? getLocalizedRegionName(deliveryAddress.region, locale) : '';
  const localizedDistrict = deliveryAddress.district && deliveryAddress.region ? 
    getLocalizedDistrictName(deliveryAddress.region, deliveryAddress.district, locale) : 
    deliveryAddress.district;
  
  const parts = [
    deliveryAddress.street_address,
    deliveryAddress.building_name,
    deliveryAddress.block && `${t('addresses.value.block', { value: deliveryAddress.block })}`,
    deliveryAddress.floor && `${t('addresses.value.floor', { value: deliveryAddress.floor })}`,
    deliveryAddress.unit && `${t('addresses.value.unit', { value: deliveryAddress.unit })}`,
    localizedDistrict,
    localizedRegion,
    // deliveryAddress.postal_code,
    t('Hong Kong'),
    deliveryAddress.country
  ].filter(Boolean);
  
  return parts.join(', ');
};

const AppointmentDetail: React.FC = () => {
  const { t, currentLanguage } = useTranslation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isPdfPreviewVisible, setIsPdfPreviewVisible] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState<{ base64?: string; url?: string; filename: string } | null>(null);
  const [regeneratingOrders, setRegeneratingOrders] = useState<Set<string>>(new Set());
  const [isAssignProviderModalVisible, setIsAssignProviderModalVisible] = useState(false);
  const [isEditingInternalRemark, setIsEditingInternalRemark] = useState(false);
  const [internalRemark, setInternalRemark] = useState('');
  const [isJdAddressModalVisible, setIsJdAddressModalVisible] = useState(false);
  const [jdDeliveryAddress, setJdDeliveryAddress] = useState('');
  const [jdDeliveryNote, setJdDeliveryNote] = useState('');
  const [jdDeliveryType, setJdDeliveryType] = useState<'standard' | 'express'>('express');
  const [pricingCheckCompleted, setPricingCheckCompleted] = useState(false);
  
  // SF Express address confirmation modal state
  const [isSfAddressModalVisible, setIsSfAddressModalVisible] = useState(false);
  const [sfDeliveryAddress, setSfDeliveryAddress] = useState('');
  const [sfDeliveryNote, setSfDeliveryNote] = useState('');
  const [sfSelectedDeliveryContactId, setSfSelectedDeliveryContactId] = useState<string | undefined>(undefined);
  
  // JD Express address confirmation modal state
  const [jdSelectedDeliveryContactId, setJdSelectedDeliveryContactId] = useState<string | undefined>(undefined);

  // Fetch appointment data
  const { data: appointmentData, loading: appointmentLoading, refetch } = useQuery<GetAppointmentQuery>(GET_APPOINTMENT, {
    variables: { id: appointmentId as string },
    skip: !appointmentId,
  });

  const appointment = appointmentData?.appointment as GetAppointmentQuery['appointment'];

  // Initialize internal remark when appointment data loads
  useEffect(() => {
    if (appointment?.internal_remark !== undefined) {
      setInternalRemark(appointment.internal_remark || '');
    }
  }, [appointment?.internal_remark]);

  const { data: historyData, loading: historyLoading } = useQuery<GetUserAppointmentsQuery>(GET_USER_APPOINTMENTS, {
    variables: { userId: appointment?.app_user_id || '' },
    skip: !appointment?.app_user_id,
    fetchPolicy: 'cache-and-network',
  });

  // Extract express orders, transactions, agoraLink, and insuranceClaims from appointment query (optional fields)
  // Note: Type assertion needed until GraphQL codegen is run to update types
  // Run `yarn codegen` to regenerate types after backend schema changes
  const rawAppointment = appointmentData?.appointment as any;
  const sfExpressOrders: SfExpressOrder[] = rawAppointment?.sfExpressOrders || [];
  const jdExpressOrders = rawAppointment?.jdExpressOrders || [];
  const transactions = rawAppointment?.transactions || [];
  const agoraLink = rawAppointment?.agoraLink;
  const insuranceClaims = rawAppointment?.insuranceClaims || [];

  // Get latest orders (already sorted by created_at DESC from repository)
  const latestSfOrder = sfExpressOrders.length > 0 ? sfExpressOrders[sfExpressOrders.length - 1] : null;
  const latestJdOrder = jdExpressOrders.length > 0 ? jdExpressOrders[jdExpressOrders.length - 1] : null;

  // Fetch delivery contact list
  const { data: deliveryContactListData } = useQuery(GET_DELIVERY_CONTACT_LIST);
  const deliveryContactList = deliveryContactListData?.deliveryContactList || [];

  // Load saved delivery contact selection from localStorage
  useEffect(() => {
    if (deliveryContactList.length > 0) {
      const savedSfContactId = localStorage.getItem('sfExpressSelectedDeliveryContactId');
      const savedJdContactId = localStorage.getItem('jdExpressSelectedDeliveryContactId');
      
      // For SF Express: use saved value or default to first contact
      if (savedSfContactId && deliveryContactList.find((c: any) => c.id === savedSfContactId)) {
        const savedContact = deliveryContactList.find((c: any) => c.id === savedSfContactId);
        if (savedContact) {
          setSfSelectedDeliveryContactId(savedSfContactId);
          setSfDeliveryAddress(savedContact.address);
        }
      } else {
        // Default to first contact if no saved value or saved value not found
        const firstContact = deliveryContactList[0];
        if (firstContact) {
          setSfSelectedDeliveryContactId(firstContact.id);
          setSfDeliveryAddress(firstContact.address);
          localStorage.setItem('sfExpressSelectedDeliveryContactId', firstContact.id);
        }
      }
      
      // For JD Express: use saved value or default to first contact
      if (savedJdContactId && deliveryContactList.find((c: any) => c.id === savedJdContactId)) {
        const savedContact = deliveryContactList.find((c: any) => c.id === savedJdContactId);
        if (savedContact) {
          setJdSelectedDeliveryContactId(savedJdContactId);
          setJdDeliveryAddress(savedContact.address);
        }
      } else {
        // Default to first contact if no saved value or saved value not found
        const firstContact = deliveryContactList[0];
        if (firstContact) {
          setJdSelectedDeliveryContactId(firstContact.id);
          setJdDeliveryAddress(firstContact.address);
          localStorage.setItem('jdExpressSelectedDeliveryContactId', firstContact.id);
        }
      }
    }
  }, [deliveryContactList]);

  const appointmentHistory = (historyData?.userAppointments || []).filter((a) => a.id !== appointment?.id && ['in_progress', 'consulted', 'completed'].includes(a.status));

  // Mutations
  const [confirmAppointment, { loading: confirming }] = useMutation(CONFIRM_APPOINTMENT);
  const [startAppointment, { loading: starting }] = useMutation(START_APPOINTMENT);
  const [completeAppointment, { loading: completing }] = useMutation(COMPLETE_APPOINTMENT);
  const [cancelAppointment, { loading: cancelling }] = useMutation(CANCEL_APPOINTMENT);
  const [markNoShow, { loading: markingNoShow }] = useMutation(MARK_APPOINTMENT_NO_SHOW);
  const [updateAppointment, { loading: updating }] = useMutation(UPDATE_APPOINTMENT);
  const [createSfExpressOrder, { loading: creatingSfOrder }] = useMutation(CREATE_SF_EXPRESS_ORDER);
  const [createJdExpressOrder, { loading: creatingJdOrder }] = useMutation(CREATE_JD_EXPRESS_ORDER);
  const [precheckJdExpressOrder, { loading: precheckingJdOrder }] = useMutation(PRECHECK_JD_EXPRESS_ORDER);
  const [cloudPrintSfExpressWaybill, { loading: printing }] = useMutation(CLOUD_PRINT_SF_EXPRESS_WAYBILL);
  const [cloudPrintJdExpressWaybill, { loading: printingJd }] = useMutation(CLOUD_PRINT_JD_EXPRESS_WAYBILL);
  const [updateAddress, { loading: updatingAddress }] = useMutation(UPDATE_ADDRESS);

  // SSE subscription for real-time appointment updates
  const handleAppointmentEvent = useCallback((event: AppointmentEvent) => {
    console.log('Appointment SSE event received:', event);
    
    // Refetch appointment data when this specific appointment is updated
    if (event.appointmentId === appointmentId && (
      event.type === 'appointment_updated' || 
      event.type === 'appointment_status_changed' ||
      event.type === 'appointment_confirmed' ||
      event.type === 'appointment_started' ||
      event.type === 'appointment_completed' ||
      event.type === 'appointment_cancelled'
    )) {
      console.log('Appointment updated via SSE, refetching data...');
      refetch();
    }
  }, [appointmentId, refetch]);

  // Subscribe to SSE events
  useAppointmentSSESubscription(handleAppointmentEvent);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'blue';
      case 'confirmed': return 'green';
      case 'in_progress': return 'orange';
      case 'consulted': return 'cyan';
      case 'completed': return 'success';
      case 'cancelled': return 'red';
      case 'no_show': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return <ClockCircleOutlined />;
      case 'confirmed': return <CheckCircleOutlined />;
      case 'in_progress': return <SyncOutlined />;
      case 'consulted': return <CheckCircleOutlined />;
      case 'completed': return <CheckCircleOutlined />;
      case 'cancelled': return <CloseCircleOutlined />;
      case 'no_show': return <ExclamationCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const handleEditAppointment = () => {
    message.info(t('appointments.editFunctionalityComingSoon'));
  };

  const handleDeleteAppointment = () => {
    Modal.confirm({
      title: t('appointments.deleteAppointment'),
      content: t('appointments.deleteConfirm', { reference: appointment?.id?.slice(-6) }),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: () => {
        message.success(t('appointments.appointmentDeleted'));
        navigate('/appointments');
      }
    });
  };

  const handleConfirmAppointment = async () => {
    if (!appointment?.id) return;
    try {
      await confirmAppointment({ variables: { id: appointment.id } });
      message.success(t('appointments.appointmentConfirmed'));
      refetch();
    } catch (err: any) {
      message.error(err?.message || t('common.error'));
    }
  };

  const handleCancelAppointment = () => {
    setIsCancelModalVisible(true);
  };

  const submitCancelAppointment = async () => {
    if (!appointment?.id) return;
    if (!cancelReason.trim()) {
      message.warning(t('appointments.provideCancelReason'));
      return;
    }
    try {
      await cancelAppointment({
        variables: {
          id: appointment.id,
          reason: cancelReason.trim(),
          cancelledBy: user?.email || `${user?.firstname || ''} ${user?.lastname || ''}`.trim() || user?.id || 'admin'
        }
      });
      setIsCancelModalVisible(false);
      setCancelReason('');
      message.success(t('appointments.appointmentCancelled'));
      refetch();
    } catch (err: any) {
      message.error(err?.message || t('common.error'));
    }
  };

  const handleStartVideoCall = async () => {
    if (appointment?.id && ['scheduled', 'confirmed'].includes(appointment.status)) {
      try {
        await startAppointment({ variables: { id: appointment.id } });
        message.success(t('appointments.videoCallStarted'));
      } catch (err: any) {
        message.error(err?.message || t('common.error'));
      }
    }
    // Use Agora link from appointment query (refetch to get latest)
    const result = await refetch();
    const link = (result.data?.appointment as any)?.agoraLink || agoraLink;
    if (link?.appId && link?.channelName && link?.token && link?.uid) {
      const params = new URLSearchParams({
        appid: link.appId,
        channel: link.channelName,
        uid: link.uid,
        role: link.role,
        token: link.token,
        appointmentId: appointment?.id || ''
      }).toString();
      window.open(`/agora-call/${appointment?.id}?${params}`, 'noopener');
    } else {
      message.error(t('agora.callStartFailed'));
    }
  };

  const handleCompleteAppointment = async () => {
    if (!appointment?.id) return;
    try {
      await completeAppointment({ variables: { id: appointment.id } });
      message.success(t('appointments.appointmentCompleted'));
      refetch();
    } catch (err: any) {
      message.error(err?.message || t('common.error'));
    }
  };

  const handleMarkConsulted = async () => {
    if (!appointment?.id) return;
    try {
      await updateAppointment({
        variables: {
          id: appointment.id,
          updateAppointmentDto: {
            status: 'consulted'
          }
        }
      });
      message.success(t('appointments.appointmentMarkedConsulted'));
      refetch();
    } catch (err: any) {
      message.error(err?.message || t('common.error'));
    }
  };

  const handleMarkNoShow = async () => {
    if (!appointment?.id) return;
    try {
      await markNoShow({ variables: { id: appointment.id } });
      message.success(t('appointments.appointmentMarkedNoShow'));
      refetch();
    } catch (err: any) {
      message.error(err?.message || t('common.error'));
    }
  };

  const handleAssignProvider = () => {
    setIsAssignProviderModalVisible(true);
  };

  const handleSaveInternalRemark = async () => {
    if (!appointment?.id) return;
    try {
      await updateAppointment({
        variables: {
          id: appointment.id,
          updateAppointmentDto: {
            internal_remark: internalRemark
          }
        }
      });
      setIsEditingInternalRemark(false);
      message.success('Internal remark updated successfully');
      refetch();
    } catch (err: any) {
      message.error(err?.message || 'Failed to update internal remark');
    }
  };

  const handleContactPatient = (phoneNumber: string) => {
    // Format phone number for WhatsApp (remove any non-digit characters except +)
    const formattedPhone = phoneNumber.replace(/[^\d+]/g, '');
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${formattedPhone}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');
  };

  const handleReadyAppointment = async () => {
    if (!appointment?.id) return;
    try {
      await updateAppointment({
        variables: {
          id: appointment.id,
          updateAppointmentDto: {
            status: 'ready'
          }
        }
      });
      message.success(t('appointments.appointmentReady'));
      refetch();
    } catch (err: any) {
      message.error(err?.message || t('common.error'));
    }
  };

  const handleCreateSfExpressOrder = async () => {
    if (!appointment?.delivery_address) {
      message.error(t('sfExpress.noDeliveryAddress'));
      return;
    }
    
    // Get the initial address value
    const initialAddress = getDeliveryAddress(appointment.delivery_address, t, currentLanguage);
    setSfDeliveryAddress(initialAddress);
    
    // Set default note from existing SF Express order if available
    const existingSfOrder = sfExpressOrders?.find((order: any) => order.notes);
    setSfDeliveryNote(existingSfOrder?.notes || '');
    
    setIsSfAddressModalVisible(true);
  };

  const submitSfExpressOrder = async () => {
    if (!appointment?.id || !appointment?.delivery_address?.id) return;
    
    if (!sfDeliveryAddress.trim()) {
      message.warning(t('sfExpress.provideDeliveryAddress'));
      return;
    }
    
    try {
      // Create the SF Express order with override address
      const result = await createSfExpressOrder({
        variables: {
          input: {
            appointmentId: appointment.id,
            notes: sfDeliveryNote.trim() || undefined,
            overrideAddress: sfDeliveryAddress.trim(),
            deliveryContactId: sfSelectedDeliveryContactId || undefined
          }
        }
      });

      if (result.data?.createSfExpressOrderFromAppointment.success) {
        const orderData = result.data.createSfExpressOrderFromAppointment.data;
        
        // Close the address modal
        setIsSfAddressModalVisible(false);
        
        message.success(
          t('sfExpress.orderCreatedSuccessfully', { orderId: orderData?.orderId || 'N/A' })
        );
        
        // Show order details in a modal
        if (orderData?.trackingNumber || orderData?.waybillNo) {
          Modal.success({
            title: t('sfExpress.orderCreated'),
            content: (
              <div>
                <p><strong>{t('sfExpress.orderId')}:</strong> {orderData.orderId}</p>
                {orderData.trackingNumber && (
                  <p><strong>{t('sfExpress.trackingNumber')}:</strong> {orderData.trackingNumber}</p>
                )}
                {orderData.waybillNo && (
                  <p><strong>{t('sfExpress.waybillNo')}:</strong> {orderData.waybillNo}</p>
                )}
              </div>
            ),
            okText: 'Download Waybill PDF',
            onOk: () => {
              // Automatically download the waybill PDF
              if (orderData?.waybillNo) {
                handleCloudPrintSfExpressWaybill(orderData.waybillNo);
              }
            }
          });
        }

        // Automatically download waybill PDF if available
        if (orderData?.waybillNo) {
          // Add a small delay to ensure the order is fully processed
          setTimeout(() => {
            handleCloudPrintSfExpressWaybill(orderData.waybillNo);
          }, 1000);
        }

        // Refetch data
        refetch();
      } else {
        const errorMsg = result.data?.createSfExpressOrderFromAppointment.errorMsg || t('sfExpress.failedToCreateOrder');
        message.error(errorMsg);
      }
    } catch (err: any) {
      message.error(err?.message || t('sfExpress.failedToCreateOrder'));
    }
  };

  const handleCreateJdExpressOrder = async () => {
    if (!appointment?.delivery_address) {
      message.error(t('appointments.jdExpress.noDeliveryAddress'));
      return;
    }
    
    // Get the initial address value
    const initialAddress = getDeliveryAddress(appointment.delivery_address, t, currentLanguage);
    setJdDeliveryAddress(initialAddress);
    
    // Set default note from existing JD Express order if available
    const existingJdOrder = jdExpressOrders?.find((order: any) => order.notes);
    setJdDeliveryNote(existingJdOrder?.notes || '');
    
    setIsJdAddressModalVisible(true);
  };

  const handlePrecheckJdExpressOrder = async () => {
    if (!appointment?.id) return;
    
    try {
      // Map delivery type to expressTypeId: 1 = standard, 2 = express
      const expressTypeId = jdDeliveryType === 'express' ? 2 : 1;
      
      const result = await precheckJdExpressOrder({
        variables: {
          input: {
            appointmentId: appointment.id,
            expressTypeId,
            overrideAddress: jdDeliveryAddress.trim() || undefined
          }
        }
      });

      if (result.data?.precheckJdExpressOrderFromAppointment.success) {
        const precheckData = result.data.precheckJdExpressOrderFromAppointment.data;
        
        // Set pricing check as completed successfully
        setPricingCheckCompleted(true);
        
        // Show precheck results in a modal
        Modal.success({
          title: t('appointments.jdExpress.precheckResults'),
          content: (
            <div>
              <p><strong>{t('appointments.jdExpress.deliveryType')}</strong> {jdDeliveryType === 'express' ? t('appointments.jdExpress.expressDelivery') : t('appointments.jdExpress.standardDelivery')}</p>
              <p><strong>{t('appointments.jdExpress.service')}:</strong> {precheckData.productName || t('common.na')}</p>
              {precheckData.totalAmount && (
                <p><strong>{t('appointments.jdExpress.estimatedTotalFee')}:</strong> {precheckData.currency} {precheckData.totalAmount}</p>
              )}
              {precheckData.freightPre && (
                <p><strong>{t('appointments.jdExpress.freight')}:</strong> {precheckData.currency} {precheckData.freightPre}</p>
              )}
              {precheckData.calWeight && (
                <p><strong>{t('appointments.jdExpress.calculatedWeight')}:</strong> {precheckData.calWeight} kg</p>
              )}
              {precheckData.deliveryPromiseTime && (
                <p><strong>{t('appointments.jdExpress.estimatedDelivery')}:</strong> {new Date(precheckData.deliveryPromiseTime).toLocaleString()}</p>
              )}
              {precheckData.startStationName && (
                <p><strong>{t('appointments.jdExpress.origin')}:</strong> {precheckData.startStationName}</p>
              )}
              {precheckData.endStationName && (
                <p><strong>{t('appointments.jdExpress.destination')}:</strong> {precheckData.endStationName}</p>
              )}
            </div>
          ),
          width: 500,
        });
      } else {
        const errorCode = result.data?.precheckJdExpressOrderFromAppointment.errorCode;
        const errorMsg = result.data?.precheckJdExpressOrderFromAppointment.errorMsg || 'Failed to precheck JD Express order';
        
        Modal.error({
          title: t('appointments.jdExpress.precheckFailed'),
          content: (
            <div>
              {errorCode && (
                <p><strong>{t('appointments.jdExpress.errorCode')}:</strong> {errorCode}</p>
              )}
              <p><strong>{t('appointments.jdExpress.message')}:</strong> {errorMsg}</p>
            </div>
          ),
        });
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to precheck JD Express order');
    }
  };

  const submitJdExpressOrder = async () => {
    if (!appointment?.id || !appointment?.delivery_address?.id) return;
    
    if (!jdDeliveryAddress.trim()) {
      message.warning(t('appointments.jdExpress.provideDeliveryAddress'));
      return;
    }
    
    try {
      // Map delivery type to expressTypeId: 1 = standard, 2 = express
      const expressTypeId = jdDeliveryType === 'express' ? 2 : 1;
      
      // Create the JD Express order with override address
      const result = await createJdExpressOrder({
        variables: {
          input: {
            appointmentId: appointment.id,
            expressTypeId,
            notes: jdDeliveryNote.trim() || undefined,
            overrideAddress: jdDeliveryAddress.trim(),
            deliveryContactId: jdSelectedDeliveryContactId || undefined
          }
        }
      });

      if (result.data?.createJdExpressOrderFromAppointment.success) {
        const orderData = result.data.createJdExpressOrderFromAppointment.data;
        
        // Close the address modal
        setIsJdAddressModalVisible(false);
        
        message.success(
          t('appointments.jdExpress.orderCreatedSuccessfully', { orderId: orderData?.orderId || 'N/A' })
        );
        
        // Show order details in a modal
        if (orderData?.trackingNumber || orderData?.waybillNo) {
          Modal.success({
            title: t('appointments.jdExpress.orderCreated'),
            content: (
              <div>
                <p><strong>{t('appointments.jdExpress.orderId')}:</strong> {orderData.orderId}</p>
                {orderData.trackingNumber && (
                  <p><strong>{t('appointments.jdExpress.trackingNumber')}:</strong> {orderData.trackingNumber}</p>
                )}
                {orderData.waybillNo && (
                  <p><strong>{t('appointments.jdExpress.waybillNumber')}:</strong> {orderData.waybillNo}</p>
                )}
                {orderData.totalFee && (
                  <p><strong>{t('appointments.jdExpress.totalFee')}:</strong> ¥{orderData.totalFee}</p>
                )}
              </div>
            ),
          });
        }

        // Refetch data
        refetch();
      } else {
        const errorCode = result.data?.createJdExpressOrderFromAppointment.errorCode;
        const errorMsg = result.data?.createJdExpressOrderFromAppointment.errorMsg || 'Failed to create JD Express order';
        
        // Show detailed error in a modal
        Modal.error({
          title: t('appointments.jdExpress.failedToCreateOrder'),
          content: (
            <div>
              {errorCode && (
                <p><strong>{t('appointments.jdExpress.errorCode')}:</strong> {errorCode}</p>
              )}
              <p><strong>{t('appointments.jdExpress.message')}:</strong> {errorMsg}</p>
            </div>
          ),
        });
        
        // Also show a brief error message
        message.error(`${errorCode ? `[${errorCode}] ` : ''}${errorMsg}`);
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to create JD Express order');
    }
  };

  const handleCloudPrintSfExpressWaybill = async (waybillNo: string, isRegeneration: boolean = false) => {
    if (!waybillNo) {
      message.error('Waybill number is required');
      return;
    }
    
    try {
      const result = await cloudPrintSfExpressWaybill({
        variables: {
          input: {
            waybillNo,
            templateCode: 'fm_210_standard_standard',
            version: '2.0',
            fileType: 'pdf',
            sync: true
          }
        }
      });

      if (result.data?.cloudPrintSfExpressWaybill.success) {
        const waybillData = result.data.cloudPrintSfExpressWaybill.data;
        
        if (waybillData?.pdfBase64) {
          // Show PDF preview with base64 data
          showPdfPreview(`waybill-${waybillNo}.pdf`, waybillData.pdfBase64);
          message.success(isRegeneration ? 'Waybill PDF regenerated successfully!' : 'Waybill PDF generated successfully!');
        } else if (waybillData?.pdfUrl) {
          // Show PDF preview with URL
          showPdfPreview(`waybill-${waybillNo}.pdf`, undefined, waybillData.pdfUrl);
          message.success(isRegeneration ? 'Waybill PDF regenerated successfully!' : 'Waybill PDF generated successfully!');
        } else {
          message.warning('PDF data not available for preview');
        }
      } else {
        const errorMsg = result.data?.cloudPrintSfExpressWaybill.errorMsg || 'Failed to generate waybill PDF';
        message.error(errorMsg);
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to generate waybill PDF');
    }
  };

  const handleCloudPrintJdExpressWaybill = async (waybillNo: string, isRegeneration: boolean = false) => {
    if (!waybillNo) {
      message.error('Waybill number is required');
      return;
    }
    
    try {
      const result = await cloudPrintJdExpressWaybill({
        variables: {
          input: {
            waybillNo,
            templateCode: 'jd_express_waybill',
            version: '2.0',
            fileType: 'pdf',
            sync: true
          }
        }
      });

      if (result.data?.cloudPrintJdExpressWaybill.success) {
        const waybillData = result.data.cloudPrintJdExpressWaybill.data;
        
        if (waybillData?.pdfBase64) {
          // Show PDF preview with base64 data
          showPdfPreview(`jd-express-waybill-${waybillNo}.pdf`, waybillData.pdfBase64);
          message.success(isRegeneration ? 'JD Express waybill PDF regenerated successfully!' : 'JD Express waybill PDF generated successfully!');
        } else if (waybillData?.pdfUrl) {
          // Show PDF preview with URL
          showPdfPreview(`jd-express-waybill-${waybillNo}.pdf`, undefined, waybillData.pdfUrl);
          message.success(isRegeneration ? 'JD Express waybill PDF regenerated successfully!' : 'JD Express waybill PDF generated successfully!');
        } else {
          message.warning('PDF data not available for preview');
        }
      } else {
        const errorMsg = result.data?.cloudPrintJdExpressWaybill.errorMsg || 'Failed to generate JD Express waybill PDF';
        message.error(errorMsg);
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to generate JD Express waybill PDF');
    }
  };

  // Helper function to download PDF from base64
  const downloadPdfFromBase64 = (base64Data: string, filename: string) => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF from base64:', error);
      message.error('Failed to download PDF');
    }
  };

  // Helper function to download PDF from URL
  const downloadPdfFromUrl = (url: string, filename: string) => {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF from URL:', error);
      message.error('Failed to download PDF');
    }
  };

  // Function to show PDF preview
  const showPdfPreview = (filename: string, base64Data?: string, url?: string) => {
    setPdfPreviewData({ base64: base64Data, url, filename });
    setIsPdfPreviewVisible(true);
  };

  // Function to regenerate PDF for an order
  const handleRegenerateSfExpressWaybill = async (order: SfExpressOrder) => {
    if (order.waybill_no) {
      // If we have a waybill number, regenerate using it
      setRegeneratingOrders(prev => new Set(prev).add(order.id));
      try {
        await handleCloudPrintSfExpressWaybill(order.waybill_no, true);
      } finally {
        setRegeneratingOrders(prev => {
          const newSet = new Set(prev);
          newSet.delete(order.id);
          return newSet;
        });
      }
    } else if (order.order_id) {
      // If no waybill but we have order ID, try to get waybill from order data
      message.info('Attempting to regenerate PDF for this order...');
      
      // You could implement a call to get the latest order status here
      // For now, we'll show a message that the order needs to be processed first
      message.warning('Order is still being processed. Please wait for waybill number to be generated.');
    } else {
      message.error('Unable to regenerate PDF: No waybill number or order ID available');
    }
  };

  if (appointmentLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('common.loading')}</div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Alert
          message={t('appointments.appointmentNotFound')}
          description={t('appointments.appointmentNotFoundDescription')}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      {/* Cancel Modal */}
      <Modal
        title={t('appointments.cancelAppointment')}
        open={isCancelModalVisible}
        onOk={submitCancelAppointment}
        okButtonProps={{ danger: true, loading: cancelling }}
        okText={t('appointments.cancelAppointment')}
        onCancel={() => { setIsCancelModalVisible(false); setCancelReason(''); }}
        cancelText={t('common.no')}
      >
        <Text style={{ display: 'block', marginBottom: 8 }}>{t('appointments.cancelConfirm', { reference: appointment?.id?.slice(-6) })}</Text>
        <TextArea
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder={t('appointments.cancelReasonPlaceholder')}
        />
      </Modal>

      {/* JD Express Address Confirmation Modal */}
      <JDExpressConfirmationModal
        open={isJdAddressModalVisible}
        onCancel={() => {
          setIsJdAddressModalVisible(false);
          setJdDeliveryNote('');
          setJdDeliveryType('standard');
          setPricingCheckCompleted(false);
          // Don't reset address and contact selection - keep them for next time
        }}
        onSubmit={submitJdExpressOrder}
        onPrecheck={handlePrecheckJdExpressOrder}
        loading={updatingAddress || creatingJdOrder}
        prechecking={precheckingJdOrder}
        pricingCheckCompleted={pricingCheckCompleted}
        deliveryType={jdDeliveryType}
        deliveryAddress={jdDeliveryAddress}
        deliveryNote={jdDeliveryNote}
        selectedDeliveryContactId={jdSelectedDeliveryContactId}
        deliveryContactList={deliveryContactList}
        onDeliveryTypeChange={(type) => {
          setJdDeliveryType(type);
          setPricingCheckCompleted(false); // Reset pricing check when delivery type changes
        }}
        onDeliveryAddressChange={setJdDeliveryAddress}
        onDeliveryNoteChange={setJdDeliveryNote}
        onDeliveryContactChange={(contactId) => {
          setJdSelectedDeliveryContactId(contactId);
          if (contactId) {
            localStorage.setItem('jdExpressSelectedDeliveryContactId', contactId);
          } else {
            localStorage.removeItem('jdExpressSelectedDeliveryContactId');
          }
        }}
      />

      {/* SF Express Address Confirmation Modal */}
      <Modal
        title={t('sfExpress.confirmDeliveryAddress')}
        open={isSfAddressModalVisible}
        onOk={submitSfExpressOrder}
        okButtonProps={{ loading: updatingAddress || creatingSfOrder }}
        okText={t('sfExpress.confirmCreateOrder')}
        onCancel={() => {
          setIsSfAddressModalVisible(false);
          setSfDeliveryNote('');
          // Don't reset address and contact selection - keep them for next time
        }}
        cancelText={t('common.cancel')}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            {t('sfExpress.deliveryAddressDescription')}
          </Text>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>{t('sfExpress.selectDeliveryContact') || 'Select Delivery Contact (Optional)'}</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder={t('sfExpress.selectDeliveryContactPlaceholder') || 'Select a delivery contact...'}
            value={sfSelectedDeliveryContactId}
            onChange={(value) => {
              setSfSelectedDeliveryContactId(value);
              if (value) {
                localStorage.setItem('sfExpressSelectedDeliveryContactId', value);
              }
            }}
            options={deliveryContactList.map((contact: any) => ({
              value: contact.id,
              label: `${contact.address} (${contact.contact})`
            }))}
          />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('sfExpress.selectDeliveryContactNote') || 'Select a contact to auto-fill the address, or enter manually below.'}
            </Text>
          </div>
        </div>
        
        <div style={{ marginBottom: 8 }}>
          <Text strong>{t('sfExpress.deliveryAddress')}</Text>
        </div>
        <TextArea
          rows={4}
          value={sfDeliveryAddress}
          onChange={(e) => setSfDeliveryAddress(e.target.value)}
          placeholder={t('sfExpress.deliveryAddressPlaceholder')}
        />
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('sfExpress.deliveryAddressNote')}
          </Text>
        </div>
        
        <div style={{ marginTop: 16, marginBottom: 8 }}>
          <Text strong>{t('sfExpress.deliveryNote')}</Text>
        </div>
        <TextArea
          rows={3}
          value={sfDeliveryNote}
          onChange={(e) => setSfDeliveryNote(e.target.value)}
          placeholder={t('sfExpress.deliveryNotePlaceholder')}
        />
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('sfExpress.deliveryNoteDescription')}
          </Text>
        </div>
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        title={`PDF Preview - ${pdfPreviewData?.filename || 'Waybill'}`}
        open={isPdfPreviewVisible}
        onCancel={() => {
          setIsPdfPreviewVisible(false);
          setPdfPreviewData(null);
        }}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => {
              if (pdfPreviewData?.base64) {
                downloadPdfFromBase64(pdfPreviewData.base64, pdfPreviewData.filename);
              } else if (pdfPreviewData?.url) {
                downloadPdfFromUrl(pdfPreviewData.url, pdfPreviewData.filename);
              }
            }}
          >
            Download PDF
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setIsPdfPreviewVisible(false);
              setPdfPreviewData(null);
            }}
          >
            Close
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {pdfPreviewData?.base64 ? (
          <iframe
            src={`data:application/pdf;base64,${pdfPreviewData.base64}`}
            width="100%"
            height="600"
            style={{ border: 'none' }}
            title="PDF Preview"
          />
        ) : pdfPreviewData?.url ? (
          <iframe
            src={pdfPreviewData.url}
            width="100%"
            height="600"
            style={{ border: 'none' }}
            title="PDF Preview"
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">PDF preview not available</Text>
          </div>
        )}
      </Modal>

      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/appointments')}
            style={{ marginBottom: 16 }}
          >
            {t('common.back')}
          </Button>
          <Title level={2} style={{ margin: 0 }}>{t('appointments.appointmentDetails')}</Title>
          <Text type="secondary">{t('appointments.appointmentDetailsSubtitle')}</Text>
        </Col>
        <Col>
          <Space>
            {appointment.status === 'scheduled' && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleConfirmAppointment}
                loading={confirming}
              >
                {t('appointments.confirm')}
              </Button>
            )}
            {/* {['confirmed'].includes(appointment.status) && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleReadyAppointment}
                loading={updating}
              >
                {t('appointments.ready')}
              </Button>
            )} */}
            {['confirmed', 'ready', 'in_progress'].includes(appointment.status) && (
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                onClick={handleStartVideoCall}
                loading={starting}
              >
                {t('appointments.startVideoCall')}
              </Button>
            )}
            {appointment.status === 'in_progress' && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleMarkConsulted}
                loading={updating}
              >
                {t('appointments.markConsulted') || 'Mark Consulted'}
              </Button>
            )}
            {appointment.status === 'consulted' && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleCompleteAppointment}
                loading={completing}
              >
                {t('appointments.complete') || 'Complete'}
              </Button>
            )}
            {['scheduled', 'confirmed'].includes(appointment.status) && (
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={handleCancelAppointment}
                loading={cancelling}
              >
                {t('appointments.cancel')}
              </Button>
            )}

          </Space>
        </Col>
      </Row>

      {/* Status Alert */}
      <Alert
        message={
          <Space>
            {getStatusIcon(appointment.status)}
            <span>{t(`appointments.statuses.${appointment.status}`)}</span>
            <Tag color={getStatusColor(appointment.status)}>
              {t(`appointments.statuses.${appointment.status}`)}
            </Tag>
          </Space>
        }
        type={appointment.status === 'cancelled' ? 'error' : 'info'}
        style={{ marginBottom: 24 }}
      />

      {/* Main Content */}
      <Row gutter={[24, 24]}>
                  {/* Basic Information */}
          <Col xs={24} lg={16}>
            <Card
              title={t('appointments.basicInformation')}
              style={{ marginBottom: 24 }}
            >
              <Descriptions column={2} bordered>
                <Descriptions.Item label={t('appointments.bookingReference')} span={2}>
                  <Text code>{appointment.id?.slice(-6)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('appointments.date')}>
                  {dayjs(appointment.date).format('YYYY/MM/DD')}
                </Descriptions.Item>
                <Descriptions.Item label={t('appointments.time')}>
                  {appointment.time}
                </Descriptions.Item>
                <Descriptions.Item label={t('appointments.appointmentType')}>
                  {t(`appointments.types.${appointment.type}`)}
                </Descriptions.Item>
                <Descriptions.Item label={t('appointments.reservationType')}>
                  {appointment.reservation_type ? t(`appointments.reservationTypes.${appointment.reservation_type}`) : t('common.na')}
                </Descriptions.Item>
                {appointment.status === 'pending' && appointment.expected_time_range && (
                  <Descriptions.Item label={t('appointments.expectedTimeRange')} span={2}>
                    <Tag color="orange">{appointment.expected_time_range}</Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Delivery Address */}
            {appointment.delivery_address && (
              <DeliveryAddressCard
                appointmentId={appointmentId}
                deliveryAddress={appointment.delivery_address}
                deliveryNote={appointment.delivery_note}
                onCreateSfExpressOrder={handleCreateSfExpressOrder}
                creatingSfOrder={creatingSfOrder}
                onCreateJdExpressOrder={handleCreateJdExpressOrder}
                creatingJdOrder={creatingJdOrder}
                jdExpressOrders={jdExpressOrders}
                onCloudPrintWaybill={handleCloudPrintJdExpressWaybill}
                locale={currentLanguage}
                sfExpressOrders={sfExpressOrders}
                onViewSfExpressPdf={(waybillNo) => {
                  handleCloudPrintSfExpressWaybill(waybillNo);
                }}
                onDeleteSfExpressOrder={() => {
                  refetch();
                }}
                onDeleteJdExpressOrder={() => {
                  refetch();
                }}
              />
            )}

          <AppointmentQuestionnaireResponseSection appointment={appointment} />

          {/* Patient Information */}
          <PatientInformationCard
            patients={appointment.patient ? [appointment.patient] : appointment.app_user?.patients}
            fallbackPhone={appointment.patient?.phone_number || appointment.patient_phone}
            fallbackEmail={appointment.patient?.email || appointment.patient_email}
            collapsible={true}
          />

          {/* Provider Information */}
          {   
            !user?.doctor_id && (
              <ProviderInformationCard
                provider={appointment.provider}
                appointmentId={appointmentId || ''}
                onProviderUpdated={refetch}
              />
            )
          }


          {/* Transaction Section */}
          <TransactionSection
            appointmentId={appointmentId || ''}
            transactions={transactions}
            transactionsLoading={appointmentLoading}
            onRefetch={refetch}
            onTransactionCreated={(transaction: any) => {
              message.success(t('transactions.createdSuccessfully'));
            }}
            onTransactionUpdated={(transaction: any) => {
              message.success(t('transactions.updatedSuccessfully'));
            }}
            onTransactionDeleted={(transactionId: string) => {
              message.success(t('transactions.deletedSuccessfully'));
            }}
          />

          {/* Insurance Claims Section */}
          <InsuranceClaimCard
            appointmentId={appointmentId || ''}
            insuranceClaims={insuranceClaims}
            insuranceClaimsLoading={appointmentLoading}
            onRefetch={refetch}
            collapsible={true}
          />
          
          {/* SF Express Orders Section */}
          {/* <SfExpressOrdersSection
            sfExpressOrders={sfExpressOrders}
            loading={appointmentLoading}
            printing={printing}
            regeneratingOrders={regeneratingOrders}
            onRefresh={refetch}
            onDownloadWaybill={handleCloudPrintSfExpressWaybill}
            onRegenerateWaybill={handleRegenerateSfExpressWaybill}
          /> */}

          {/* Document Upload */}
          <DocumentUpload
            appointmentId={appointmentId || ''}
            onDocumentUploaded={() => {
              // Optionally refresh appointment data or show success message
              message.success(t('appointments.documents.uploadedSuccessfully'));
            }}
          />

          {/* Notes */}
          {appointment.appointment_notes && (
            <Card
              title={t('appointments.notes')}
              style={{ marginBottom: 24 }}
            >
              <Text>{appointment.appointment_notes}</Text>
            </Card>
          )}

          {/* Cancellation Information */}
          {appointment.cancelled_reason && (
            <Card
              title={t('appointments.cancellationInformation')}
              style={{ marginBottom: 24 }}
            >
              <Alert
                message={t('appointments.cancelledReason')}
                description={appointment.cancelled_reason}
                type="error"
                showIcon
              />
            </Card>
          )}
        </Col>

        {/* Sidebar */}
        <Col xs={24} lg={8}>
          {/* Patient Appointment History */}
          {appointment.app_user_id && (
            <Card title={t('appointments.patientHistory') || 'Patient Appointment History'} style={{ marginBottom: 24 }}>
              {historyLoading ? (
                <Spin size="small" />
              ) : appointmentHistory.length === 0 ? (
                <Text type="secondary">{t('appointments.noHistory') || 'No past appointments'}</Text>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {appointmentHistory
                    .sort((a, b) => new Date(b.date as unknown as string).getTime() - new Date(a.date as unknown as string).getTime())
                    .slice(0, 5)
                    .map((hist) => (
                      <div key={hist.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text strong>{dayjs(hist.date).format('YYYY/MM/DD')} {hist.time}</Text>
                          <Space size={6} wrap>
                            <Tag color={getStatusColor(hist.status)}>{t(`appointments.statuses.${hist.status}`)}</Tag>
                            <Tag>{t(`appointments.consultationTypes.${hist.consultation_type}`)}</Tag>
                          </Space>
                          {(hist as any).questionnaire?.chief_complaint && (
                            <Text type="secondary">{(hist as any).questionnaire.chief_complaint}</Text>
                          )}
                        </Space>
                      </div>
                    ))}
                </Space>
              )}
            </Card>
          )}

          {/* Payment Information */}
          {/* <PaymentInformationCard
            paymentStatus={appointment.payment_status || 'pending'}
            paymentMethod={appointment.payment_method || undefined}
            totalAmount={appointment.total_amount || undefined}
            showCreateTransaction={true}
            appointmentId={appointment.id}
            onTransactionCreated={(transaction) => {
              console.log('New transaction created:', transaction);
              // Here you can add logic to handle the new transaction
              // For example, update the appointment data or send to backend
              message.success(`Transaction ${transaction.reference} created successfully`);
            }}
          /> */}

          {/* Internal Remark */}
          <Card
            title={
              <Space>
                <span>Internal Remark</span>
                {!isEditingInternalRemark && (
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditingInternalRemark(true)}
                  >
                    Edit
                  </Button>
                )}
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            {isEditingInternalRemark ? (
              <div>
                <TextArea
                  rows={4}
                  value={internalRemark}
                  onChange={(e) => setInternalRemark(e.target.value)}
                  placeholder="Enter internal remark..."
                  style={{ marginBottom: 16, whiteSpace: 'pre-wrap' }}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleSaveInternalRemark}
                    loading={updating}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingInternalRemark(false);
                      setInternalRemark(appointment.internal_remark || '');
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              </div>
            ) : (
              <Text style={{ whiteSpace: 'pre-wrap' }}>
                {appointment.internal_remark || 'No internal remark added yet.'}
              </Text>
            )}
          </Card>

                    {/* Timeline */}
          <AppointmentTimeline appointment={appointment} />

          {/* Quick Actions */}
          <AppointmentQuickActions
            appointment={appointment}
            loadingStates={{
              starting,
              updating,
              completing,
              cancelling,
              markingNoShow,
              assigningProvider: false
            }}
            handlers={{
              onStartVideoCall: handleStartVideoCall,
              onMarkConsulted: handleMarkConsulted,
              onCompleteAppointment: handleCompleteAppointment,
              onCancelAppointment: handleCancelAppointment,
              onMarkNoShow: handleMarkNoShow,
              onAssignProvider: handleAssignProvider,
              onContactPatient: handleContactPatient
            }}
            showNoShowButton={false}
            showAssignProviderButton={true}
            showContactPatientButton={true}
          />
        </Col>
      </Row>

      {/* Provider Selection Modal */}
      {isAssignProviderModalVisible && appointment?.id && (
        <ProviderSelectionModal
          visible={isAssignProviderModalVisible}
          appointmentId={appointment.id}
          onCancel={() => setIsAssignProviderModalVisible(false)}
          onAssigned={() => {
            setIsAssignProviderModalVisible(false);
            refetch();
            message.success(t('appointments.assignedSuccessfully'));
          }}
        />
      )}

    </div>
  );
};

export default AppointmentDetail; 
